import React from 'react';
import { Link } from 'react-router-dom';

import LegalDocumentPage, { Section, SectionTitle } from '../components/LegalDocumentPage';
import { LEGAL, LEGAL_ROUTES } from '../data/legalContent';

const PrivacyPolicy = () => (
  <LegalDocumentPage
    title="Privacy Policy"
    description="Privacy-first: local storage by default, no on-site analytics."
    path={LEGAL_ROUTES.privacy}
    lastUpdated={LEGAL.lastUpdated}
  >
    <Section>
      <p>
        {LEGAL.siteName} ({LEGAL.siteUrl}) helps founders understand how they lead, decide, and execute through a
        self-assessment and optional report delivery. We are privacy-first: quiz results stay on your device unless you
        choose to email or share them. We do not run third-party analytics on this website.
      </p>
    </Section>

    <Section>
      <SectionTitle>Who is responsible for your data?</SectionTitle>
      <p>
        The operator of {LEGAL.siteName} is responsible for processing described here. For privacy requests, see our{' '}
        <Link to={LEGAL_ROUTES.contact}>Contact</Link> page or email{' '}
        <a href={`mailto:${LEGAL.privacyEmail}`}>{LEGAL.privacyEmail}</a>.
      </p>
    </Section>

    <Section>
      <SectionTitle>What we collect</SectionTitle>
      <ul>
        <li>
          <strong>Quiz responses and results (local by default):</strong> Your answers, calculated founder type,
          percentages, check-ins, and run history are stored in your browser&apos;s local storage unless you export,
          email, or share them.
        </li>
        <li>
          <strong>Email address (optional):</strong> If you choose &quot;Send my report,&quot; we process your email
          to deliver your report through our email provider (Resend). With your consent, we may also add you to our
          mailing audience for product updates.
        </li>
        <li>
          <strong>Payment data (if enabled later):</strong> If paid features are enabled, payment processing is handled
          by Stripe. We do not store full card numbers on our servers.
        </li>
        <li>
          <strong>Server logs:</strong> Our hosting provider (Vercel) may process technical logs (IP address, user agent,
          timestamps) for security and reliability.
        </li>
      </ul>
    </Section>

    <Section>
      <SectionTitle>What we do not do</SectionTitle>
      <ul>
        <li>No Plausible, Google Analytics, or similar tracking scripts on the site</li>
        <li>No advertising or cross-site profiling cookies</li>
        <li>No sale of personal data</li>
      </ul>
      <p>
        We may use Google Search Console to review search performance. That is a site-owner tool and does not install
        analytics cookies on your browser when you visit {LEGAL.siteName}.
      </p>
    </Section>

    <Section>
      <SectionTitle>Why we process data (legal bases)</SectionTitle>
      <ul>
        <li>
          <strong>Provide the service:</strong> Local storage of quiz results is necessary to show your report,
          check-ins, and history on your device.
        </li>
        <li>
          <strong>Email delivery:</strong> When you request a report by email, we process your email with your consent
          to perform that request.
        </li>
        <li>
          <strong>Security and compliance:</strong> Protecting the service and meeting legal obligations.
        </li>
      </ul>
    </Section>

    <Section>
      <SectionTitle>Third-party processors</SectionTitle>
      <ul>
        <li><strong>Vercel</strong> (hosting, serverless API routes)</li>
        <li><strong>Resend</strong> (transactional email and optional audience list)</li>
        <li><strong>Stripe</strong> (payments, if enabled)</li>
      </ul>
      <p>
        These providers may process data in various countries. Where required, we rely on appropriate safeguards offered
        by vendors.
      </p>
    </Section>

    <Section>
      <SectionTitle>Retention</SectionTitle>
      <ul>
        <li>Local browser data remains until you clear it, use &quot;Delete all local data&quot; in your report, or clear browser site data.</li>
        <li>Email delivery logs are retained as needed for service operation and legal compliance, then deleted or anonymized.</li>
      </ul>
    </Section>

    <Section>
      <SectionTitle>Your rights</SectionTitle>
      <p>Depending on your location, you may have the right to:</p>
      <ul>
        <li>Access the personal data we hold about you</li>
        <li>Request correction or deletion</li>
        <li>Object to or restrict certain processing</li>
        <li>Withdraw consent (for optional email marketing)</li>
        <li>Data portability (export your local backup JSON from the app)</li>
        <li>Lodge a complaint with your local privacy regulator, where applicable</li>
      </ul>
      <p>
        To exercise rights related to email or server-side data, email{' '}
        <a href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</a>. To erase on-device data immediately, use
        <strong> Delete all local data</strong> in Share &amp; save on your results page, or clear site data in your browser.
      </p>
    </Section>

    <Section>
      <SectionTitle>Children</SectionTitle>
      <p>
        {LEGAL.siteName} is intended for founders and adults. You must meet the minimum age described in our{' '}
        <Link to={LEGAL_ROUTES.terms}>Terms of Service</Link> to use the service.
      </p>
      <p>
        Quiz answers and results stay on your device by default. We do not knowingly collect personal data on our
        servers from anyone below that age. The only way we receive data from you is if you choose to email your
        report or contact us.
      </p>
      <p>
        If you believe a child has submitted an email or other data to us, email{' '}
        <a href={`mailto:${LEGAL.privacyEmail}`}>{LEGAL.privacyEmail}</a> and we will delete it. For on-device
        data, clear site data in the browser or use <strong>Delete all local data</strong> on the results page.
      </p>
    </Section>

    <Section>
      <SectionTitle>Changes</SectionTitle>
      <p>
        We may update this policy. Material changes will be reflected by the &quot;Last updated&quot; date above.
      </p>
      <p>
        See also our <Link to={LEGAL_ROUTES.terms}>Terms of Service</Link>,{' '}
        <Link to={LEGAL_ROUTES.cookies}>Storage policy</Link>, and{' '}
        <Link to={LEGAL_ROUTES.contact}>Contact</Link>.
      </p>
    </Section>
  </LegalDocumentPage>
);

export default PrivacyPolicy;
